import { useEffect, useRef } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import UpsellTrigger from '@alltrails/analytics/enums/UpsellTrigger';
import useUser from 'hooks/useUser';
import ProductType from '@alltrails/analytics/enums/ProductType';
import logUpsellScrollStarted from '@alltrails/analytics/events/logUpsellScrollStarted';
import logUpsellShown from '@alltrails/analytics/events/logUpsellShown';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import logWebProModalDismissed from '@alltrails/analytics/events/logWebProModalDismissed';
import logWebProModalPurchaseButtonTapped from '@alltrails/analytics/events/logWebProModalPurchaseButtonTapped';
import DismissAction from '@alltrails/analytics/enums/DismissAction';
import { Modal } from '@alltrails/modules/Modal';
import '@alltrails/modules/Modal/index.css';
import { wrapUrlSafe } from '@alltrails/shared/utils/languageSupport';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import PurchaseTier from 'types/PurchaseTier';
import ProductsAvailable from '@alltrails/analytics/enums/ProductsAvailable';
import useExposureEvent from 'hooks/useExposureEvent';
import * as styles from './BaseUpsellModal.module.scss';

type Props = {
  children?: React.ReactNode;
  contentsClassName?: string;
  isOpen: boolean;
  onCloseRequest: () => void;
  primaryButtonText?: React.ReactNode;
  returnTo?: string;
  upsellTrigger: UpsellTrigger;
  tierSelected: PurchaseTier;
  productsAvailable: ProductsAvailable;
};

export default function BaseUpsellStep({
  children,
  contentsClassName,
  onCloseRequest,
  primaryButtonText,
  returnTo,
  upsellTrigger,
  isOpen,
  tierSelected,
  productsAvailable
}: Props) {
  const user = useUser();
  const isPromoEligible = user?.isPromoEligible;
  const languageRegionCode = useLanguageRegionCode();
  const hasScrolled = useRef(false);
  const sendExposure = useExposureEvent('growth-rm-price-testing-2025');

  useEffect(() => {
    if (isOpen) {
      sendExposure();
    }
  }, [isOpen, sendExposure]);

  const params = new URLSearchParams();

  if (returnTo) {
    params.set('returnTo', returnTo);
  }

  if (tierSelected === 'peak') {
    params.set('tier', 'peak');
  }

  const ctaHref = `/plans/checkout${params.toString() ? `?${params.toString()}` : ''}`;

  useEffect(() => {
    if (isOpen) {
      logUpsellShown({
        purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
        trigger: upsellTrigger,
        products_available: productsAvailable
      });
    }
  }, [isOpen, isPromoEligible, productsAvailable, upsellTrigger]);

  const handleCloseRequest = () => {
    logWebProModalDismissed({
      dismiss_action: DismissAction.XOut,
      trigger: upsellTrigger,
      purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
      products_available: productsAvailable
    });
    onCloseRequest();
  };

  const handleCtaClick = () => {
    logWebProModalPurchaseButtonTapped({
      product_type: tierSelected === 'peak' ? ProductType.Peak : ProductType.Plus,
      purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
      trigger: upsellTrigger
    });

    window.location.assign(wrapUrlSafe(`/plans/checkout?tier=${tierSelected}`, languageRegionCode));
  };

  const primaryButton = {
    onClick: handleCtaClick,
    text:
      primaryButtonText ??
      (isPromoEligible ? <FormattedMessage defaultMessage="Start your free trial" /> : <FormattedMessage defaultMessage="Upgrade to AllTrails+" />),
    testId: 'checkout-cta',
    linkInfo: {
      href: ctaHref
    }
  };

  return (
    <Modal
      renderInPortal
      buttons={{
        experimental: true,
        primaryButton
      }}
      isOpen={isOpen}
      caption={<FormattedMessage defaultMessage="Cancel anytime" />}
      onCloseRequest={handleCloseRequest}
      contentsClassName={contentsClassName}
      onScroll={() => {
        if (!hasScrolled.current) {
          logUpsellScrollStarted({
            purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
            trigger: upsellTrigger
          });

          hasScrolled.current = true;
        }
      }}
    >
      <div className={styles.contents}>{children}</div>
    </Modal>
  );
}
